import classNames from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { Badge, Flex, Popover, Text } from '@mantine/core';

import { useObjectLoad } from '@/hooks/useObjectLoad';

import { getPlaceMapPathClass } from '@/shared/helpers/utils';
import useBookingStore from '@/shared/store/booking.store.ts';
import useUserStore from '@/shared/store/user.store.ts';
import { TDateRange } from '@/shared/types/app.types';
import { IBookingObjects } from '@/shared/types/bookingObjects.types';

import classes from './PlaceMapPath.module.css';

interface PathProps {
	item: IBookingObjects;
	personId: number | null;
	date?: TDateRange;
	isSingle?: boolean;
}

export const PlaceMapPath = ({ item, personId, date, isSingle = false }: PathProps) => {
	const isAdmin = useUserStore((state) => state.isAdmin);
	const isModerator = useUserStore((state) => state.isModerator);
	const { bookingPlace, setBookingPlace, setBookingLocation } = useBookingStore();
	const isPersonal = !!item?.owner_person;
	const isOffice = item?.type.need_a_key;
	const isBookedByPerson = item?.bookings?.some((booking) => booking.client_id === personId);
	const kind = item.type.kind;

	const [percent, setPercent] = useState(0);

	const { objectLoad } = useObjectLoad({
		objectId: item.id,
		date: date,
		enabled: kind === 'location' && !!date,
	});

	const getLoadColorClass = (percent: number) => {
		switch (true) {
			case percent < 86:
				return classes.interactive;

			case percent < 100:
				return classes.twothirdavailable;

			default:
				return classes.unavailable;
		}
	};

	const classnames =
		kind === 'location'
			? [getLoadColorClass(percent)]
			: [
					classes[getPlaceMapPathClass(item, personId ?? undefined, isAdmin, isModerator)],
					{
						[classes.selected]: bookingPlace && bookingPlace?.id === item?.id,
						[classes.blink]: isSingle || isBookedByPerson,
					},
				];

	const content = (
		<path
			onClick={() => {
				if (isSingle || item?.is_blocked) return;
				if (!item?.type.is_bookable) {
					setBookingLocation(item);
					return;
				}
				setBookingPlace(item);
			}}
			key={item?.id}
			d={item?.svg}
			stroke={item?.type.is_bookable ? 'none' : '#1da57a'}
			strokeWidth={5}
			className={classNames(classes.root, ...classnames)}
		/>
	);

	const PersonalPlace = () => (
		<Flex direction="column">
			<Text size="lg" fw={600}>
				Персональное место
			</Text>
			<Text c="dimmed" size="md" fw={600}>
				{item?.owner_person.full_name}
			</Text>
		</Flex>
	);

	useEffect(() => {
		if (objectLoad) {
			Object.keys(objectLoad).map((place) => {
				if (place === 'location') {
					return;
				}
				const object = { ...objectLoad[place as keyof typeof objectLoad], type: place };

				switch (place) {
					case 'parking_place':
					case 'work_place': {
						setPercent(
							object.booked && object.total ? Math.round(object.booked / (object.total / 100)) : 0,
						);

						break;
					}
				}
			});
		}
	}, [objectLoad]);

	if (isAdmin || isModerator) {
		// if (item?.booking_status !== 'free' || isPersonal) {
		if (item?.booking_status !== 'free' || (isPersonal && !item.is_available_for_person)) {
			return (
				<Popover position="top" shadow="md" clickOutsideEvents={['mouseup', 'touchend']} withArrow>
					<Popover.Target>{content}</Popover.Target>
					<Popover.Dropdown style={{ pointerEvents: 'none' }}>
						{isPersonal ? (
							<PersonalPlace />
						) : (
							<>
								<Text size="lg" fw={600}>
									{item?.name}
								</Text>
								<Flex {...(isAdmin || isModerator ? { direction: 'column' } : {})} gap={10}>
									{item?.bookings?.map((booking) => (
										<Flex key={booking?.id} align="center" gap={10}>
											{(isAdmin || isModerator) && (
												<Flex maw={220}>
													<Text c="dimmed" size="sm" fw={600}>
														{booking.client?.full_name ?? 'Нет данных'}
													</Text>
												</Flex>
											)}
											<Badge size="sm" variant="light" color="green">
												{`${dayjs(booking.start).format('DD MMM')}`}
											</Badge>
											<Badge size="sm" variant="light" color="orange">
												{`${dayjs(booking?.start).format('HH:mm')} - ${dayjs(booking?.end).format('HH:mm')}`}
											</Badge>
										</Flex>
									))}
								</Flex>
							</>
						)}
					</Popover.Dropdown>
				</Popover>
			);
		} else {
			return content;
		}
	}

	return (
		<>
			{!item.is_available_for_person || item.booking_status !== 'free' ? (
				<Popover position="top" withArrow shadow="md" clickOutsideEvents={['mouseup', 'touchend']}>
					<Popover.Target>{content}</Popover.Target>
					<Popover.Dropdown style={{ pointerEvents: 'none' }}>
						<Text size="lg" fw={600}>
							{item?.name}
						</Text>
						<Flex gap={10}>
							{item?.bookings?.map((booking) => (
								<Flex key={booking?.id} align="center" gap={10}>
									{item.type.kind !== 'parking_place' && (
										<Flex maw={220}>
											<Text c="dimmed" size="sm" fw={600}>
												{booking.client?.full_name ?? 'Нет данных'}
											</Text>
										</Flex>
									)}
									<Badge size="sm" variant="light" color="green">
										{`${dayjs(booking.start).format('DD MMM')}`}
									</Badge>
									<Badge size="sm" variant="light" color="orange">
										{`${dayjs(booking.start).format('HH:mm')} - ${dayjs(booking.end).format('HH:mm')}`}
									</Badge>
								</Flex>
							))}
						</Flex>
					</Popover.Dropdown>
				</Popover>
			) : (
				content
			)}
		</>
	);
};
